import React, { useEffect, useState, useRef } from 'react';
import { displayStatus, evaluationPercentage } from '../../lib/evaluationIntegrity';
import { ScoreSummary } from '../../components/EvaluationIntegrity';
import { useParams, useNavigate } from 'react-router-dom';
import { locationsAPI, clientsAPI } from '../../api/client';
import { VENUE_TYPES, STATUS_CONFIG, MAPS_KEY } from '../../lib/locationConstants';
import { EVENT_TYPES, EVENT_STATUS } from '../../lib/eventConstants';
import { getLocationPhotoUrl } from '../../lib/fileUrls';
import { useAuth } from '../../context/AuthContext';
import SemaforoBadge from '../../components/SemaforoBadge';
import StatusBadge from '../../components/StatusBadge';
import { VersionTrendChart } from '../../components/ScoreChart';
// #UbicacionUnify — UbicacionTab combina mapa+nearby+emergencias
import UbicacionTab from '../../components/UbicacionTab';
import {
  ArrowLeft,
  Edit2,
  MapPin,
  Users,
  DoorOpen,
  Phone,
  FileText,
  Upload,
  ChevronLeft,
  ChevronRight,
  Building2,
  X,
  ExternalLink,
  FolderKanban,
  Calendar,
} from 'lucide-react';
import { toast } from 'sonner';

function LocStatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.inactiva;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold border ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function PhotoGallery({ photos, onUpload, canEdit, locationId }) {
  const [current, setCurrent] = useState(0);
  const [showManager, setShowManager] = useState(false);
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // #LocationPhotoMgmt — progreso visible: cuántas se han subido, cuántas
  // fallaron. Antes Promise.all explotaba todo el batch si una sola fallaba.
  const [progress, setProgress] = useState({ done: 0, total: 0, failed: [] });
  const [deletingUrl, setDeletingUrl] = useState(null);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploading(true);
    setProgress({ done: 0, total: files.length, failed: [] });

    // #LocationPhotoMgmt — Antes Promise.all paralelizaba todo y un timeout
    // o error en uno mataba el batch entero. Ahora secuencial con per-file
    // error handling: cada archivo se sube uno a uno; si falla, se anota
    // y se sigue con los demás.
    const failed = [];
    let succeeded = 0;
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      try {
        await locationsAPI.uploadPhoto(locationId, f);
        succeeded += 1;
        setProgress({ done: succeeded, total: files.length, failed });
      } catch (err) {
        const detail =
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          'Error desconocido';
        failed.push({ name: f.name, error: detail });
        setProgress({ done: succeeded, total: files.length, failed });
        // eslint-disable-next-line no-console
        if (process.env.NODE_ENV !== 'production') console.error(`[uploadPhoto] ${f.name}:`, err);
      }
    }

    // Resumen final
    if (failed.length === 0) {
      toast.success(`${succeeded} foto${succeeded === 1 ? '' : 's'} subida${succeeded === 1 ? '' : 's'}`);
    } else if (succeeded > 0) {
      toast.warning(
        `${succeeded} subida${succeeded === 1 ? '' : 's'}, ${failed.length} fallida${failed.length === 1 ? '' : 's'}: ${failed.map(f => f.name).join(', ')}`,
        { duration: 10000 }
      );
    } else {
      const firstErr = failed[0]?.error || 'Error desconocido';
      toast.error(`No se subió ninguna foto: ${firstErr}`, { duration: 10000 });
    }

    setUploading(false);
    setProgress({ done: 0, total: 0, failed: [] });
    onUpload();
    // Reset input para permitir re-subir los mismos archivos si quiere
    if (fileRef.current) fileRef.current.value = '';
  };

  // #LocationPhotoMgmt — eliminar foto individual del gallery manager
  const handleDelete = async (photoUrl) => {
    if (!window.confirm('¿Eliminar esta foto? Esta acción no se puede deshacer.')) return;
    setDeletingUrl(photoUrl);
    try {
      await locationsAPI.deletePhoto(locationId, photoUrl);
      toast.success('Foto eliminada');
      onUpload();
      // Si la foto eliminada era la "current" del carousel, retrocede
      setCurrent(c => Math.max(0, Math.min(c, photos.length - 2)));
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Error desconocido';
      toast.error(`No se pudo eliminar: ${detail}`);
    } finally {
      setDeletingUrl(null);
    }
  };

  if (!photos?.length) {
    return (
      <div className="h-64 bg-slate-100 rounded-sm flex flex-col items-center justify-center gap-3 text-slate-300">
        <Building2 size={48} />
        <p className="text-sm text-slate-400">Sin fotos</p>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              className="soem-btn-secondary px-4 py-2 text-xs flex items-center gap-2">
              <Upload size={13} /> {uploading ? `Subiendo ${progress.done}/${progress.total}...` : 'Subir fotos'}
            </button>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="relative h-64 rounded-sm overflow-hidden group">
        {(() => {
          const photoUrl = photos[current];
          const src = getLocationPhotoUrl(photoUrl);
          return (
            <img src={src} alt={`foto ${current + 1}`}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300"><rect fill="%23e2e8f0" width="400" height="300"/><text fill="%2394a3b8" x="50%" y="50%" text-anchor="middle" dy=".3em">Imagen no disponible</text></svg>'; }} />
          );
        })()}
        {photos.length > 1 && (
          <>
            <button onClick={() => setCurrent(p => (p - 1 + photos.length) % photos.length)}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronLeft size={16} />
            </button>
            <button onClick={() => setCurrent(p => (p + 1) % photos.length)}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
              <ChevronRight size={16} />
            </button>
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1">
              {photos.map((_, i) => (
                <button key={i} onClick={() => setCurrent(i)}
                  className={`w-2 h-2 rounded-full ${i === current ? 'bg-white' : 'bg-white/50'}`} />
              ))}
            </div>
          </>
        )}
        <div className="absolute top-2 right-2 flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
          {/* #LocationPhotoMgmt — Botón "Gestionar" que abre vista grid */}
          <button
            onClick={() => setShowManager(true)}
            className="bg-black/60 text-white px-3 py-1.5 rounded-sm text-xs flex items-center gap-1.5"
            data-testid="manage-photos-btn">
            🗂️ Gestionar ({photos.length})
          </button>
          {canEdit && (
            <>
              <input ref={fileRef} type="file" multiple accept="image/*" className="hidden" onChange={handleUpload} />
              <button onClick={() => fileRef.current?.click()} disabled={uploading}
                className="bg-black/60 text-white px-3 py-1.5 rounded-sm text-xs flex items-center gap-1.5">
                <Upload size={12} /> {uploading ? `${progress.done}/${progress.total}...` : 'Subir fotos'}
              </button>
            </>
          )}
        </div>
        {/* Progress bar overlay durante upload */}
        {uploading && progress.total > 0 && (
          <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-3 py-2">
            <div className="flex items-center justify-between text-white text-xs mb-1">
              <span>Subiendo {progress.done + progress.failed.length} / {progress.total}</span>
              {progress.failed.length > 0 && <span className="text-red-300">{progress.failed.length} falló</span>}
            </div>
            <div className="bg-white/20 h-1 rounded">
              <div className="bg-soem-primary h-1 rounded transition-all"
                style={{ width: `${((progress.done + progress.failed.length) / progress.total) * 100}%` }} />
            </div>
          </div>
        )}
      </div>

      {/* #LocationPhotoMgmt — Modal manager: grid de thumbnails con delete */}
      {showManager && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
          onClick={() => setShowManager(false)}>
          <div className="bg-white rounded-sm shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
              <h3 className="font-bold text-white text-sm">Gestionar fotos ({photos.length})</h3>
              <button onClick={() => setShowManager(false)} aria-label="Cerrar">
                <X size={18} className="text-slate-400 hover:text-white" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-y-auto">
              {canEdit && (
                <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-sm px-3 py-2">
                  <p className="text-xs text-blue-800">
                    Tip: la primera foto se usa como portada de la locación.
                  </p>
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                    className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5">
                    <Upload size={12} /> {uploading ? `${progress.done}/${progress.total}...` : 'Subir más'}
                  </button>
                </div>
              )}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {photos.map((url, idx) => (
                  <div key={url || idx} className="relative group/photo aspect-square rounded-sm overflow-hidden border border-slate-200">
                    <img
                      src={getLocationPhotoUrl(url)}
                      alt={`foto ${idx + 1}`}
                      className="w-full h-full object-cover"
                      onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect fill="%23e2e8f0" width="200" height="200"/><text fill="%2394a3b8" x="50%" y="50%" text-anchor="middle" dy=".3em">No disponible</text></svg>'; }}
                    />
                    {/* Badge "Portada" en la primera */}
                    {idx === 0 && (
                      <div className="absolute top-1 left-1 bg-soem-primary text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                        Portada
                      </div>
                    )}
                    {/* Botones de acción on hover */}
                    {canEdit && (
                      <div className="absolute inset-0 bg-black/0 group-hover/photo:bg-black/40 transition-colors flex items-end justify-center pb-2 opacity-0 group-hover/photo:opacity-100">
                        <button
                          onClick={() => handleDelete(url)}
                          disabled={deletingUrl === url}
                          className="bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 rounded-sm flex items-center gap-1.5 disabled:opacity-60"
                          data-testid={`delete-photo-${idx}`}>
                          {deletingUrl === url ? 'Eliminando...' : (<><X size={12} /> Eliminar</>)}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default function LocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isLider } = useAuth();
  const canEdit = isAdmin || isLider;

  const [location, setLocation] = useState(null);
  const [evaluations, setEvaluations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [events, setEvents] = useState([]);
  const [clientName, setClientName] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');

  const load = async () => {
    setLoading(true);
    try {
      const [locRes, evalsRes, projsRes, eventsRes] = await Promise.all([
        locationsAPI.get(id),
        locationsAPI.getEvaluations(id).catch(() => ({ data: { items: [] } })),
        locationsAPI.getProjects(id).catch(() => ({ data: { items: [] } })),
        locationsAPI.getEvents(id).catch(() => ({ data: { items: [] } })),
      ]);
      setLocation(locRes.data);
      setEvaluations(evalsRes.data.items || evalsRes.data || []);
      setProjects(projsRes.data.items || projsRes.data || []);
      setEvents(eventsRes.data.items || eventsRes.data || []);
      if (locRes.data.client_id) {
        clientsAPI.list().then(r => {
          const c = (r.data.items || []).find(c => c.id === locRes.data.client_id);
          if (c) setClientName(c.name);
        }).catch(() => {});
      }
    } catch { toast.error('Error al cargar locación'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleStatusChange = async (status) => {
    try {
      await locationsAPI.changeStatus(id, status);
      toast.success('Status actualizado');
      load();
    } catch { toast.error('Error al cambiar status'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-4 border-soem-primary border-t-transparent" /></div>;
  if (!location) return <div className="text-center py-12 text-slate-400">Locación no encontrada.</div>;

  const vt = VENUE_TYPES[location.venue_type] || { label: location.venue_type, emoji: '🏢' };
  const addr = location.address || {};
  const gps = location.gps_location;
  const publishedEvals = evaluations.filter(e => e.status === 'publicada').sort((a, b) => a.version - b.version);

  const addressFormatted = [
    addr.street && `${addr.street}${addr.exterior_number ? ' ' + addr.exterior_number : ''}`,
    addr.colony,
    addr.city,
    addr.state,
    addr.postal_code && `CP ${addr.postal_code}`,
  ].filter(Boolean).join(', ');

  return (
    <div className="space-y-6 animate-fade-in" data-testid="location-detail">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button onClick={() => navigate('/dashboard/locations')} className="soem-btn-secondary p-2 mt-1">
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-2xl font-bold text-soem-navy">{location.name}</h1>
            <LocStatusBadge status={location.status} />
            <span className="bg-slate-100 text-slate-500 text-xs px-2 py-0.5 rounded font-mono">{location.location_code}</span>
          </div>
          <p className="text-slate-500 text-sm mt-0.5">{vt.emoji} {vt.label} · {addr.city || '—'}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {canEdit && (
            <>
              <select className="text-sm border border-slate-300 rounded-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-soem-primary bg-white"
                value={location.status} aria-label="Cambiar estado de locación" onChange={e => handleStatusChange(e.target.value)}>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <button onClick={() => navigate(`/dashboard/locations/${id}/edit`)}
                className="soem-btn-secondary px-4 py-2 text-sm flex items-center gap-2"
                data-testid="edit-location-btn">
                <Edit2 size={14} /> Editar
              </button>
            </>
          )}
          {gps && (
            <a href={`https://www.google.com/maps?q=${gps.lat},${gps.lng}`} target="_blank" rel="noopener noreferrer"
              className="soem-btn-secondary px-3 py-2 text-sm flex items-center gap-1.5">
              <ExternalLink size={14} /> Ver en mapa
            </a>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Gallery */}
        <div className="lg:col-span-1">
          <PhotoGallery photos={location.photos} locationId={id}
            canEdit={canEdit} onUpload={load} />
          {/* Quick stats */}
          <div className="mt-4 grid grid-cols-3 gap-3">
            <div className="soem-card p-3 text-center">
              <p className="text-lg font-black text-soem-navy">{evaluations.length || location.evaluations_count || 0}</p>
              <p className="soem-label">Evaluaciones</p>
            </div>
            <div className="soem-card p-3 text-center">
              <p className="text-lg font-black text-soem-navy">{projects.length || location.projects_count || 0}</p>
              <p className="soem-label">Proyectos</p>
            </div>
            <div className="soem-card p-3 text-center">
              <p className="text-lg font-black text-soem-navy">{events.length || 0}</p>
              <p className="soem-label">Eventos</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="lg:col-span-2">
          <div className="flex gap-1 border-b border-slate-200 mb-4">
            {/* #UbicacionUnify — tab "Emergencias" reemplazado por
                "Ubicación" que combina mapa + servicios cercanos +
                emergencias del venue. */}
            {[
              { id: 'info', label: 'Información General' },
              { id: 'ubicacion', label: 'Ubicación' },
              { id: 'evaluaciones', label: `Evaluaciones (${evaluations.length})` },
              { id: 'proyectos', label: `Proyectos (${projects.length})` },
              { id: 'eventos', label: `Eventos (${events.length})` },
            ].map(t => (
              <button key={t.id} onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors ${activeTab === t.id ? 'border-soem-primary text-soem-primary' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                data-testid={`tab-${t.id}`}>
                {t.label}
              </button>
            ))}
          </div>

          {/* Tab: Info */}
          {activeTab === 'info' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                {location.phone && (
                  <div>
                    <p className="soem-label mb-1 flex items-center gap-1"><Phone size={11} /> Teléfono</p>
                    <p className="text-sm text-soem-navy font-medium">{location.phone}</p>
                  </div>
                )}
                <div>
                  <p className="soem-label mb-1">Tipo de Espacio</p>
                  <p className="text-sm text-soem-navy font-medium">{vt.emoji} {vt.label}</p>
                </div>
                <div>
                  <p className="soem-label mb-1 flex items-center gap-1"><Users size={11} /> Aforo Máximo Legal</p>
                  <p className="text-sm text-soem-navy font-medium">{(location.max_capacity || 0).toLocaleString()} personas</p>
                </div>
                {location.total_sqm && (
                  <div>
                    <p className="soem-label mb-1">Metros Cuadrados</p>
                    <p className="text-sm text-soem-navy font-medium">{location.total_sqm.toLocaleString()} m²</p>
                  </div>
                )}
                <div>
                  <p className="soem-label mb-1 flex items-center gap-1"><DoorOpen size={11} /> Puntos de Acceso</p>
                  <div className="flex gap-4 text-sm">
                    <span className="font-medium text-soem-navy">{location.access_points?.entrances || 0} entradas</span>
                    <span className="text-slate-300">·</span>
                    <span className="font-medium text-soem-navy">{location.access_points?.exits || 0} salidas</span>
                  </div>
                </div>
                {clientName && (
                  <div>
                    <p className="soem-label mb-1">Cliente Asociado</p>
                    <p className="text-sm text-soem-navy font-medium">{clientName}</p>
                  </div>
                )}
                {location.notes && (
                  <div>
                    <p className="soem-label mb-1 flex items-center gap-1"><FileText size={11} /> Notas</p>
                    <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-sm border">{location.notes}</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {addressFormatted && (
                  <div>
                    <p className="soem-label mb-1 flex items-center gap-1"><MapPin size={11} /> Dirección</p>
                    <p className="text-sm text-soem-navy">{addressFormatted}</p>
                  </div>
                )}
                {gps && (
                  <div>
                    <p className="soem-label mb-2">Ubicación en Mapa</p>
                    <div className="rounded-sm overflow-hidden border border-slate-200">
                      <iframe
                        title="mapa locacion"
                        src={`https://www.google.com/maps/embed/v1/place?key=${MAPS_KEY}&q=${gps.lat},${gps.lng}&zoom=15`}
                        width="100%" height="220" style={{ border: 0 }}
                        allowFullScreen loading="lazy"
                      />
                    </div>
                    <p className="text-xs text-slate-400 font-mono mt-1">{gps.lat}, {gps.lng}</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tab: Evaluaciones */}
          {activeTab === 'evaluaciones' && (
            <div className="space-y-5">
              {publishedEvals.length >= 2 && (
                <div className="soem-card p-4">
                  <p className="font-bold text-soem-navy text-sm mb-2">Evolución del Puntaje</p>
                  <VersionTrendChart evaluations={publishedEvals.map(e => ({
                    ...e, general_fields: { instalacion: location.name }
                  }))} />
                </div>
              )}
              {evaluations.length === 0 ? (
                <div className="soem-card p-10 text-center">
                  <p className="text-slate-400">Esta locación no tiene evaluaciones registradas aún.</p>
                </div>
              ) : (
                <div className="soem-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="soem-table-header">
                        <th className="px-4 py-3 text-left">Proyecto</th>
                        <th className="px-4 py-3 text-left">Versión</th>
                        <th className="px-4 py-3 text-left">Puntaje</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {evaluations.map(e => (
                        <tr key={e.id} className="soem-table-row">
                          <td className="px-4 py-3 text-slate-600">{e.project_name || '—'}</td>
                          <td className="px-4 py-3 font-mono text-soem-navy">v{e.version}</td>
                          <td className="px-4 py-3">
                            <SemaforoBadge semaforo={e.semaforo} percentage={evaluationPercentage(e)} size="sm" />
                          </td>
                          <td className="px-4 py-3">
                            <StatusBadge status={displayStatus(e)} />
                            <ScoreSummary evaluation={e} />
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-xs">
                            {e.published_at ? new Date(e.published_at).toLocaleDateString('es-MX') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tab: Proyectos */}
          {activeTab === 'proyectos' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-slate-500 text-sm">{projects.length} proyecto(s) en esta locación</p>
                {(isAdmin || isLider) && location.status === 'activa' && (
                  <button onClick={() => navigate('/dashboard/proyectos', { state: { location_id: id, location_name: location.name, client_id: location.client_id || null, status: location.status } })}
                    className="soem-btn-primary px-3 py-1.5 text-xs flex items-center gap-1.5">
                    <FolderKanban size={12} /> Crear proyecto aquí
                  </button>
                )}
              </div>
              {projects.length === 0 ? (
                <div className="soem-card p-10 text-center">
                  <FolderKanban size={36} className="text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400">No hay proyectos vinculados a esta locación.</p>
                </div>
              ) : (
                <div className="soem-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="soem-table-header">
                        <th className="px-4 py-3 text-left">Nombre</th>
                        <th className="px-4 py-3 text-left">Cliente</th>
                        <th className="px-4 py-3 text-left">Status</th>
                        <th className="px-4 py-3 text-left">Fecha</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projects.map(p => (
                        <tr key={p.id} className="soem-table-row cursor-pointer hover:bg-blue-50/40"
                          onClick={() => navigate(`/dashboard/proyectos/${p.id}`)}>
                          <td className="px-4 py-3 font-semibold text-soem-navy">{p.name}</td>
                          <td className="px-4 py-3 text-slate-500">{p.client_name || '—'}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.status === 'activo' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                              {p.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-slate-400 text-xs">
                            {p.created_at ? new Date(p.created_at).toLocaleDateString('es-MX') : '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Tab: Eventos */}
          {activeTab === 'eventos' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-slate-500 text-sm">{events.length} evento(s) en esta locación</p>
              </div>
              {events.length === 0 ? (
                <div className="soem-card p-10 text-center">
                  <Calendar size={36} className="text-slate-200 mx-auto mb-3" />
                  <p className="text-slate-400">No hay eventos vinculados a esta locación.</p>
                </div>
              ) : (
                <div className="soem-card overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="soem-table-header">
                        <th className="px-4 py-3 text-left">Código</th>
                        <th className="px-4 py-3 text-left">Nombre</th>
                        <th className="px-4 py-3 text-left">Tipo</th>
                        <th className="px-4 py-3 text-left">Fecha</th>
                        <th className="px-4 py-3 text-left">Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {events.map(evt => {
                        const et = EVENT_TYPES[evt.event_type] || { emoji: '📅', label: evt.event_type };
                        const stCfg = EVENT_STATUS[evt.status] || EVENT_STATUS.planeacion;
                        return (
                          <tr key={evt.id} className="soem-table-row cursor-pointer hover:bg-blue-50/40"
                            onClick={() => navigate(`/dashboard/events/${evt.id}`)}>
                            <td className="px-4 py-3 font-mono text-xs text-slate-400">{evt.event_code}</td>
                            <td className="px-4 py-3 font-semibold text-soem-navy">{evt.name}</td>
                            <td className="px-4 py-3 text-xs text-slate-500">{et.emoji} {et.label}</td>
                            <td className="px-4 py-3 text-slate-400 text-xs">
                              {evt.dates?.event_start ? new Date(evt.dates.event_start).toLocaleDateString('es-MX') : '—'}
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${stCfg.bg}`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${stCfg.dot}`} />{stCfg.label}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* #UbicacionUnify — Tab Ubicación reemplaza Emergencias */}
          {activeTab === 'ubicacion' && (
            <UbicacionTab mode="location" locationId={id} location={location} />
          )}
        </div>
      </div>
    </div>
  );
}
